/**
 * The device-bound half of the mobile control plane client (ADR-029, rung 1).
 * All validation and gate decisions live in release-status-core.ts (pure, and
 * tested by tests/mobile/release-status.test.ts); this file owns the IO:
 * fetch, storage, platform, and the process-lifetime flag cache.
 *
 * One launch call to `GET /mobile/release-status` carries three payloads with
 * three different failure rules:
 *
 * - **flags** fall back to the bundled defaults below — a flag the server
 *   doesn't return (or a fetch that fails outright) can only ever put the app
 *   back to how it shipped;
 * - **the store-update gate fails OPEN** — no response ever blocks a launch;
 * - **the OTA instruction fails CLOSED** — null, missing, malformed, or not
 *   matching this binary all mean "do not check for updates".
 *
 * This module is also THE TRIGGER (PLAN.md §1): the launch call that can act
 * on the server's answer. Once a binary ships carrying expo-updates, this
 * call is the only way a dormant OTA mechanism can ever be woken, which is
 * why it exists before the mechanism does.
 */

import Constants from "expo-constants";
import { Platform } from "react-native";

import { request } from "@/api/client";
import {
  decideGate,
  parseReleaseStatus,
  type GateVerdict,
  type ReleaseStatus,
} from "@/lib/release-status-core";
import { storage } from "@/lib/storage";

export type { GateVerdict, ReleaseStatus, UpdateStatus } from "@/lib/release-status-core";

/**
 * Bundled flag defaults — the values the app uses until (and unless) the
 * server says otherwise. Every remote flag MUST have a row here: a flag
 * without a default is a flag whose off-state is undefined.
 */
export const FLAG_DEFAULTS: Record<string, unknown> = {};

/** Last server-provided flags, empty until the first successful fetch. */
let remoteFlags: Record<string, unknown> = {};

/**
 * Read a flag. Server value if the launch fetch succeeded and returned one;
 * bundled default otherwise. Synchronous on purpose — callers must never wait
 * on the network to decide behaviour.
 */
export function getFlag<T>(key: string, fallback: T): T {
  if (key in remoteFlags) return remoteFlags[key] as T;
  if (key in FLAG_DEFAULTS) return FLAG_DEFAULTS[key] as T;
  return fallback;
}

const BUCKET_KEY = "opncue.rollout-bucket";
const DISMISSED_KEY = "opncue.update-dismissed-build";

/**
 * The device's rollout bucket: rolled once, stored, stable across launches
 * (a prompt that appears, vanishes and reappears is worse than no prompt).
 * Locally generated so no identifier ever leaves the device and the server
 * response stays cacheable. A ramp mechanism, never an entitlement — clearing
 * app storage re-rolls it, and that is fine (PLAN.md §2.5).
 */
async function rolloutBucket(): Promise<number> {
  const saved = await storage.get(BUCKET_KEY);
  const parsed = saved === null ? NaN : Number.parseInt(saved, 10);
  if (Number.isInteger(parsed) && parsed >= 0 && parsed < 100) return parsed;
  const rolled = Math.floor(Math.random() * 100);
  await storage.set(BUCKET_KEY, String(rolled));
  return rolled;
}

/** This binary's build number, as sent in X-Opncue-Build. Null on web. */
function ownBuild(): string | null {
  if (Platform.OS === "web") return null;
  return Constants.nativeBuildVersion ?? null;
}

/**
 * Fetch and validate the server's answer. Any failure — network, non-2xx,
 * malformed body — returns null, and every caller treats null as "no answer":
 * flags keep their defaults, the gate shows nothing, OTA does nothing.
 */
export async function fetchReleaseStatus(): Promise<ReleaseStatus | null> {
  let raw: unknown;
  try {
    raw = await request<unknown>("/mobile/release-status", { auth: false });
  } catch {
    return null;
  }
  const parsed = parseReleaseStatus(raw);
  if (parsed) remoteFlags = parsed.flags;
  return parsed;
}

/** Gather this device's context and let the pure core decide. */
export async function gateVerdict(
  status: ReleaseStatus | null,
): Promise<GateVerdict> {
  return decideGate(status, {
    platform: Platform.OS,
    bucket: await rolloutBucket(),
    dismissedBuild: await storage.get(DISMISSED_KEY),
    ownBuild: ownBuild(),
  });
}

/** Remember that the user dismissed the prompt while on this build. */
export async function dismissUpdatePrompt(): Promise<void> {
  const build = ownBuild();
  if (build !== null) await storage.set(DISMISSED_KEY, build);
}

/** One check per process: an instruction that repeats must not re-download. */
let otaCheckStarted = false;

/**
 * The OTA trigger (ADR-029 §2.3, live since Stage 3). Every step fails
 * CLOSED — the instruction is ignored unless every gate positively passes:
 *
 * 1. an instruction exists (null is the steady state and every failure path);
 * 2. this is a native binary with `expo-updates` enabled — web and dev
 *    builds (where the module is inert) never check;
 * 3. the instruction's `runtimeVersion` matches THIS binary's exactly — an
 *    update built for another native layer must never be offered here;
 * 4. the device's stable bucket is under the emergency ramp threshold;
 * 5. the server (our host, our certificate) actually has a manifest.
 *
 * Downloaded updates launch on next open — no forced reload. Interrupting a
 * live session is worse than one more launch on the broken bundle, and next
 * open is exactly when `expo-updates` applies the downloaded update.
 */
export function actOnOtaInstruction(ota: ReleaseStatus["ota"]): void {
  if (ota === null) return; // steady state, and every failure path
  if (Platform.OS === "web") return;
  if (otaCheckStarted) return;
  otaCheckStarted = true;

  void (async () => {
    try {
      const Updates = await import("expo-updates");
      if (!Updates.isEnabled) return; // dev client / debug build — inert
      if (
        typeof ota.runtimeVersion !== "string" ||
        ota.runtimeVersion !== Updates.runtimeVersion
      ) {
        return; // built for a different native layer, or malformed
      }
      if ((await rolloutBucket()) >= ota.rolloutPercent) return;

      const check = await Updates.checkForUpdateAsync();
      if (!check.isAvailable) return;
      await Updates.fetchUpdateAsync();
      // Applied on next launch. Deliberately no reloadAsync().
    } catch {
      // Every failure — module missing, host unreachable, signature
      // rejected — means "no update", never a crash on the launch path.
    }
  })();
}
